import React from 'react';
import { PowerProfileDataPoint } from '../types';

/**
 * Detects the data point interval in minutes
 * @param data - Power profile data points
 * @returns Interval in minutes between data points
 */
const detectDataPointInterval = (data: PowerProfileDataPoint[] | null | undefined): number => {
  if (!data || data.length < 2) {
    return 1;
  }

  const first = data[0].timestamp;
  const second = data[1].timestamp;

  // Check if it's our custom format (dayIndex:HH:MM)
  if (first.includes(':') && !first.includes('T')) {
    const parseTimestamp = (ts: string) => {
      const parts = ts.split(':');
      const dayIndex = parseInt(parts[0]);
      const hour = parseInt(parts[1]);
      const minute = parseInt(parts[2]);
      return dayIndex * 24 * 60 + hour * 60 + minute; // Total minutes
    };

    const firstMinutes = parseTimestamp(first);
    const secondMinutes = parseTimestamp(second);
    return secondMinutes - firstMinutes;
  }

  // For ISO timestamps
  const firstDate = new Date(first);
  const secondDate = new Date(second);
  const gapMs = secondDate.getTime() - firstDate.getTime();
  return gapMs / (60 * 1000); // Convert to minutes
};

/**
 * Calculates the xAxis interval to show one tick per day (every 24 hours)
 * Works with any data point spacing from 1 minute to 1 hour
 * @param data - Power profile data points
 * @returns xAxis interval value (0-indexed)
 */
export const calculateDailyTickInterval = (data: PowerProfileDataPoint[] | null | undefined): number => {
  if (!data || data.length < 2) {
    return 0;
  }

  const intervalMinutes = detectDataPointInterval(data);

  if (intervalMinutes <= 0) {
    return 0;
  }

  // Calculate how many data points in 24 hours
  const dataPointsPer24Hours = (24 * 60) / intervalMinutes;

  // Return interval (subtract 1 because it's 0-indexed)
  return Math.round(dataPointsPer24Hours) - 1;
};

/**
 * Creates a tick formatter function that shows weekday labels at appropriate intervals
 * Automatically detects data point spacing and shows labels at midnight (00:00)
 * @param data - Power profile data points
 * @returns Tick formatter function
 */
export const formatXAxisTick = (data?: PowerProfileDataPoint[] | null): ((value: string, index: number) => string) => {
  return (value: string): string => {
    // Check if it's our custom format (dayIndex:HH:MM)
    if (value.includes(':') && !value.includes('T')) {
      const parts = value.split(':');
      const dayIndex = parseInt(parts[0]);
      const hour = parseInt(parts[1]);
      const minute = parseInt(parts[2]);

      // Show day label ONLY at midnight (00:00)
      if (hour === 0 && minute === 0) {
        const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return dayNames[dayIndex] || '';
      }
      return '';
    }

    // Fallback to original logic for ISO timestamps
    const date = new Date(value);
    const hour = date.getHours();
    const minute = date.getMinutes();

    // Show day label ONLY at midnight (00:00)
    if (hour === 0 && minute === 0) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return '';
  };
};

/**
 * Converts Y coordinate to power value based on chart dimensions and power range
 * @param y - Y coordinate relative to chart area
 * @param chartHeight - Height of the chart area
 * @param minPower - Minimum power value in the chart
 * @param maxPower - Maximum power value in the chart
 * @returns Power value corresponding to the Y coordinate
 */
export const yToPowerValue = (y: number, chartHeight: number, minPower: number, maxPower: number): number => {
  const relativeY = y / chartHeight;
  // Recharts uses standard Y coordinate system: top=max, bottom=min
  const powerValue = maxPower - relativeY * (maxPower - minPower);
  return powerValue;
};

/**
 * Calculates chart area dimensions and mouse position relative to chart
 * @param chartRef - Reference to the chart container
 * @param event - Mouse event
 * @returns Object with chart dimensions and mouse position, or null if invalid
 */
export const calculateChartPosition = (
  chartRef: React.RefObject<HTMLDivElement>,
  event: { clientX: number; clientY: number }
): {
  chartAreaX: number;
  chartAreaY: number;
  chartAreaWidth: number;
  chartAreaHeight: number;
  chartWidth: number;
  chartHeight: number;
} | null => {
  if (!chartRef.current) {
    return null;
  }

  const rect = chartRef.current.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  const chartWidth = rect.width;
  const chartHeight = rect.height;

  // Account for chart margins (top: 10, right: 40, left: 0, bottom: 0)
  const chartAreaWidth = chartWidth - 40; // Subtract right margin
  const chartAreaHeight = chartHeight - 10; // Subtract top margin
  const chartAreaX = x; // No left margin adjustment needed
  const chartAreaY = y - 10; // Adjust for top margin

  if (chartAreaX < 0 || chartAreaX > chartAreaWidth || chartAreaY < 0 || chartAreaY > chartAreaHeight) {
    return null;
  }

  return {
    chartAreaX,
    chartAreaY,
    chartAreaWidth,
    chartAreaHeight,
    chartWidth,
    chartHeight,
  };
};

/**
 * Gets power value limits from chart data
 * @param chartData - Chart data array
 * @returns Object with min and max power values
 */
export const getPowerLimits = (chartData: PowerProfileDataPoint[]): { minPower: number; maxPower: number } => {
  const powerValues = chartData.map((d) => d.power);
  const dataMinPower = Math.min(...powerValues);
  const dataMaxPower = Math.max(...powerValues);

  // Use the same limits as Recharts (no margins) to match the visual chart
  const minPower = dataMinPower;
  const maxPower = dataMaxPower;

  return { minPower, maxPower };
};

/**
 * Converts X coordinate to data point index based on chart dimensions
 * @param x - X coordinate relative to chart area
 * @param chartWidth - Width of the chart area
 * @param dataLength - Total number of data points
 * @returns Index of the closest data point
 */
/**
 * NEW APPROACH: Find closest point using Recharts' actual point positions
 * This function will be called from Recharts' onMouseMove event
 */
